import { useEffect, useState } from "react";
import { useZoneEditorStore } from "../../state/zone-editor-store.js";
import { computeFitToPage, computeFitToWidth, applyZoomFactor } from "./zone-math.js";
import "./ZoneViewportControls.css";

/**
 * R-125 (5.2) — Zones viewport controls. Renders a compact toolbar with
 * "Fit Page", "Fit Width", a % zoom stepper, and ↑/↓ stepper buttons.
 *
 * The caller passes the actual canvas DOM size (CSS pixels) so fit math
 * uses the live viewport. We avoid a ResizeObserver here — the controls
 * call into the store on each click, and the canvas re-renders against
 * the resulting viewport on the next animation frame.
 */
interface ZoneViewportControlsProps {
  getCanvasSize: () => { width: number; height: number } | null;
  getPageSize: () => { width: number; height: number } | null;
}

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4.0;
const ZOOM_STEP = 0.1;

function clampZoom(v: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v));
}

export function ZoneViewportControls({
  getCanvasSize,
  getPageSize,
}: ZoneViewportControlsProps) {
  const viewport = useZoneEditorStore((s) => s.viewport);
  const setViewport = useZoneEditorStore((s) => s.setViewport);

  // The numeric input keeps a local string draft so the operator can clear it
  // and type "120" without each keystroke being re-clamped.
  const [draft, setDraft] = useState<string>(() => `${Math.round(viewport.scale * 100)}`);
  useEffect(() => {
    setDraft(`${Math.round(viewport.scale * 100)}`);
  }, [viewport.scale]);

  const applyAbsoluteZoom = (zoomPct: number) => {
    const canvas = getCanvasSize();
    if (!canvas) return;
    const next = applyZoomFactor(viewport, clampZoom(zoomPct / 100), canvas.width, canvas.height);
    setViewport(next);
  };

  const handleFitPage = () => {
    const canvas = getCanvasSize();
    const page = getPageSize();
    if (!canvas || !page) return;
    setViewport(computeFitToPage(canvas.width, canvas.height, page.width, page.height));
  };

  const handleFitWidth = () => {
    const canvas = getCanvasSize();
    const page = getPageSize();
    if (!canvas || !page) return;
    setViewport(computeFitToWidth(canvas.width, canvas.height, page.width, page.height));
  };

  const handleStep = (dir: 1 | -1) => {
    const target = clampZoom(viewport.scale + dir * ZOOM_STEP);
    applyAbsoluteZoom(target * 100);
  };

  const handleCommit = () => {
    const parsed = parseFloat(draft);
    if (!isFinite(parsed) || parsed <= 0) {
      setDraft(`${Math.round(viewport.scale * 100)}`);
      return;
    }
    applyAbsoluteZoom(parsed);
  };

  return (
    <div className="zvc" role="toolbar" aria-label="Zoom controls">
      <button
        type="button"
        className="zvc-btn"
        onClick={handleFitPage}
        title="Fit entire page in view (F)"
      >
        Fit Page
      </button>
      <button
        type="button"
        className="zvc-btn"
        onClick={handleFitWidth}
        title="Fit page width to viewport (W)"
      >
        Fit Width
      </button>
      <div className="zvc-divider" />
      <button
        type="button"
        className="zvc-step"
        onClick={() => handleStep(-1)}
        title="Zoom out"
        aria-label="Zoom out"
      >
        −
      </button>
      <div className="zvc-input-wrap">
        <input
          type="number"
          className="zvc-input"
          aria-label="Zoom percent"
          min={Math.round(ZOOM_MIN * 100)}
          max={Math.round(ZOOM_MAX * 100)}
          step={Math.round(ZOOM_STEP * 100)}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleCommit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
        />
        <span className="zvc-input-suffix">%</span>
      </div>
      <button
        type="button"
        className="zvc-step"
        onClick={() => handleStep(1)}
        title="Zoom in"
        aria-label="Zoom in"
      >
        +
      </button>
    </div>
  );
}
